import { Chip, useTheme } from '@mui/material';
import { ReactNode, useId } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DemandKpiValues } from 'pages/insights-pro/performance-tab/reports/demand-monitoring/queryHooks';
import { trackMatomoEvent } from 'services/matomo.service';
import { ChipLevels, getChipSxStylingForLevel } from 'styles/utils';
import { TbIcon } from 'components/Figma';
import { FilterableCountCard } from 'components/FilterableCountCard';

export interface DemandTrendFilterCardProps {
  attribute: 'category' | 'product';
  direction: 'increase' | 'decrease';
  values: DemandKpiValues;
  selected?: boolean;
  onClick: () => void;
  testid?: string;
}

export function DemandTrendFilterCard({
  attribute,
  direction,
  values,
  onClick,
  selected = false,
  testid = '',
}: DemandTrendFilterCardProps) {
  const intl = useIntl();
  const theme = useTheme();
  const instanceId = useId();

  const isIncrease = direction === 'increase';

  const handleCardClick = () => {
    trackMatomoEvent({
      action: 'DemandTrendFilterCard',
      name: {
        defaultNamePrefix: 'demand-kpi',
        details: [attribute, direction],
        testid,
        instanceId,
      },
      includePageContextInAction: true,
    });
    onClick();
  };

  const getChip = (): ReactNode => {
    const naturalValue = isIncrease ? values.increaseGrowth : values.decreaseGrowth;
    const compareValue = isIncrease ? naturalValue : naturalValue * -1;

    let chipLevel: ChipLevels;
    let iconColour = '';

    if (compareValue > 0) {
      chipLevel = 'Success';
      iconColour = theme.palette.success.main;
    } else if (compareValue < 0) {
      chipLevel = 'Critical';
      iconColour = theme.palette.error.main;
    } else {
      chipLevel = 'Neutral';
    }

    let trendIcon: ReactNode;

    if (naturalValue > 0) {
      trendIcon = <TbIcon name="TrendUp" color={iconColour} />;
    } else if (naturalValue < 0) {
      trendIcon = <TbIcon name="TrendDown" color={iconColour} />;
    } else {
      trendIcon = <TbIcon name="TrendFlat" />;
    }

    return (
      <Chip
        size="small"
        sx={getChipSxStylingForLevel(chipLevel)}
        label={
          <div>
            {trendIcon}{' '}
            {intl.formatNumber(isIncrease ? values.increaseGrowth : values.decreaseGrowth, {
              style: 'decimal',
              signDisplay: 'never',
            })}
          </div>
        }
      />
    );
  };

  return (
    <FilterableCountCard
      title={
        <FormattedMessage
          id={`demand.home.kpi.with.${isIncrease ? 'increase' : 'decrease'}`}
          values={{ attribute: intl.formatMessage({ id: `insights.${attribute}` }) }}
        />
      }
      count={isIncrease ? values.increased : values.decreased}
      chip={getChip()}
      comparisonLabel={<FormattedMessage id="demand.home.kpi.wow.ordered.items" />}
      selected={selected}
      onClick={handleCardClick}
      testId={testid}
    />
  );
}
